import { useEffect, useMemo, type ReactNode } from 'react'
import { useForm, type DefaultValues } from 'react-hook-form'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'

import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { cn } from '@/lib/utils'

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const optionalTrimmedString = z.string().transform(value => value.trim())

export const personFormSchema = z.object({
  firstName: z
    .string()
    .trim()
    .min(1, 'First name is required')
    .transform(value => value.trim()),
  lastName: z
    .string()
    .trim()
    .min(1, 'Last name is required')
    .transform(value => value.trim()),
  email: z
    .string()
    .transform(value => value.trim())
    .superRefine((value, ctx) => {
      if (!value) return
      if (!EMAIL_REGEX.test(value)) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Enter a valid email' })
      }
    }),
  phone: z.string().transform(value => value.trim()),
  notes: z.string().transform(value => value.trim()),
  displayCity: optionalTrimmedString,
  displayState: optionalTrimmedString,
  mailingAddress: optionalTrimmedString,
  mailingCity: optionalTrimmedString,
  mailingState: optionalTrimmedString,
  mailingZipCode: optionalTrimmedString,
  ahstcMember: z.boolean(),
  ahstcJudge: z.boolean(),
})

export type PersonFormValues = z.infer<typeof personFormSchema>

export interface PersonFormProps {
  onSubmit: (values: PersonFormValues) => Promise<void> | void
  defaultValues?: Partial<PersonFormValues>
  disabled?: boolean
  children?: ReactNode
}

export function PersonForm({
  onSubmit,
  defaultValues,
  disabled = false,
  children,
}: PersonFormProps) {
  const resolvedDefaults = useMemo<DefaultValues<PersonFormValues>>(() => {
    return {
      firstName: defaultValues?.firstName ?? '',
      lastName: defaultValues?.lastName ?? '',
      email: defaultValues?.email ?? '',
      phone: defaultValues?.phone ?? '',
      notes: defaultValues?.notes ?? '',
      displayCity: defaultValues?.displayCity ?? '',
      displayState: defaultValues?.displayState ?? '',
      mailingAddress: defaultValues?.mailingAddress ?? '',
      mailingCity: defaultValues?.mailingCity ?? '',
      mailingState: defaultValues?.mailingState ?? '',
      mailingZipCode: defaultValues?.mailingZipCode ?? '',
      ahstcMember: defaultValues?.ahstcMember ?? false,
      ahstcJudge: defaultValues?.ahstcJudge ?? false,
    }
  }, [defaultValues])

  const form = useForm<PersonFormValues>({
    resolver: zodResolver(personFormSchema),
    defaultValues: resolvedDefaults,
  })

  const isMember = form.watch('ahstcMember')

  useEffect(() => {
    if (!isMember && form.getValues('ahstcJudge')) {
      form.setValue('ahstcJudge', false, { shouldDirty: true })
    }
  }, [isMember, form])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)} className="flex h-full flex-col" noValidate>
        <div className="flex-1 space-y-4 overflow-y-auto pr-1">
          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="firstName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>First name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Ada"
                      disabled={disabled}
                      autoComplete="given-name"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="lastName"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Last name</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      placeholder="Lovelace"
                      disabled={disabled}
                      autoComplete="family-name"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="email"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Email</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      onChange={event => field.onChange(event.target.value)}
                      type="email"
                      placeholder="person@example.com"
                      autoComplete="email"
                      disabled={disabled}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="phone"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Phone</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      onChange={event => field.onChange(event.target.value)}
                      type="tel"
                      placeholder="555-0100"
                      autoComplete="tel"
                      disabled={disabled}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="notes"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Notes</FormLabel>
                <FormControl>
                  <Textarea
                    {...field}
                    value={field.value ?? ''}
                    onChange={event => field.onChange(event.target.value)}
                    placeholder="Add background, preferences, or intake notes"
                    disabled={disabled}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="displayCity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Display city</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      onChange={event => field.onChange(event.target.value)}
                      placeholder="Hillsboro"
                      disabled={disabled}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="displayState"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Display state</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      onChange={event => field.onChange(event.target.value)}
                      placeholder="OR"
                      disabled={disabled}
                      className="uppercase"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <FormField
              control={form.control}
              name="mailingAddress"
              render={({ field }) => (
                <FormItem className="sm:col-span-2">
                  <FormLabel>Mailing address</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      onChange={event => field.onChange(event.target.value)}
                      placeholder="123 Honeycomb Way"
                      disabled={disabled}
                      autoComplete="street-address"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="mailingCity"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mailing city</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      onChange={event => field.onChange(event.target.value)}
                      placeholder="Hillsboro"
                      disabled={disabled}
                      autoComplete="address-level2"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="mailingState"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mailing state</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      onChange={event => field.onChange(event.target.value)}
                      placeholder="OR"
                      disabled={disabled}
                      autoComplete="address-level1"
                      className="uppercase"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name="mailingZipCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Mailing ZIP</FormLabel>
                  <FormControl>
                    <Input
                      {...field}
                      value={field.value ?? ''}
                      onChange={event => field.onChange(event.target.value)}
                      placeholder="97124"
                      disabled={disabled}
                      autoComplete="postal-code"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <FormField
            control={form.control}
            name="ahstcMember"
            render={({ field }) => (
              <FormItem
                className={cn(
                  'flex items-start gap-3 rounded-md border border-dashed border-border/60 bg-muted/40 px-3 py-2',
                  disabled ? 'opacity-70' : 'hover:border-border'
                )}
              >
                <FormControl>
                  <input
                    type="checkbox"
                    className="mt-1 h-4 w-4 rounded border border-input"
                    checked={field.value}
                    onChange={event => field.onChange(event.target.checked)}
                    aria-label="AHSTC member"
                    disabled={disabled}
                  />
                </FormControl>
                <div className="space-y-1">
                  <FormLabel className="text-sm font-medium">AHSTC member</FormLabel>
                  <p className="text-xs text-muted-foreground">
                    Track membership status for roster exports and show-specific eligibility.
                  </p>
                </div>
              </FormItem>
            )}
          />

          {isMember ? (
            <FormField
              control={form.control}
              name="ahstcJudge"
              render={({ field }) => (
                <FormItem
                  className={cn(
                    'flex items-start gap-3 rounded-md border border-dashed border-border/60 bg-muted/40 px-3 py-2',
                    disabled ? 'opacity-70' : 'hover:border-border'
                  )}
                >
                  <FormControl>
                    <input
                      type="checkbox"
                      className="mt-1 h-4 w-4 rounded border border-input"
                      checked={field.value}
                      onChange={event => field.onChange(event.target.checked)}
                      aria-label="AHSTC judge"
                      disabled={disabled}
                    />
                  </FormControl>
                  <div className="space-y-1">
                    <FormLabel className="text-sm font-medium">AHSTC judge</FormLabel>
                    <p className="text-xs text-muted-foreground">
                      Track certified judges for staffing and intake validation.
                    </p>
                  </div>
                </FormItem>
              )}
            />
          ) : null}
        </div>

        {children ? <div className="pt-4">{children}</div> : null}
      </form>
    </Form>
  )
}
